import { useEffect } from 'react';
import {
  StackLayout,
  HeadingText,
  ParagraphText,
  useMediaQuery,
  queryHelpers,
  ImageMessage,
} from '@leagueplatform/genesis-core';
import {
  useGetUserProfile,
  LoadingSpinner,
  DETAIL,
} from '@telus-alberta-health-web/shared-module';
import {
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
  trackAnalyticsEvent,
} from '@leagueplatform/analytics';
import { useIntl } from '@leagueplatform/locales';
import { BaseModalContainer } from 'components/modals/common/base-modal.component';
import { useDeleteAccount } from 'hooks/use-delete-account.hook';

import exitWarning from 'assets/exit-warning.svg';
import { ErrorState } from '@leagueplatform/web-common-components';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { QUERY_PARAM_KEYS, QUERY_PARAM_MODAL_VALUE } from 'common/constants';
import { ModalActionButton } from './common/modal-action-button.component';

export const DeleteAccountModal = ({ onClose }: { onClose: () => void }) => {
  const { formatMessage } = useIntl();
  useEffect(() => {
    trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
    });
  }, []);

  const isMobile = useMediaQuery(queryHelpers.only('mobile'));
  const {
    mutateAsync,
    isLoading: isAccountDeleteLoading,
    isError,
    reset,
  } = useDeleteAccount();
  const { data, isSuccess, isLoading, error } = useGetUserProfile();

  if (isLoading) {
    return (
      <BaseModalContainer>
        <LoadingSpinner />
      </BaseModalContainer>
    );
  }

  if (!isSuccess || isError) {
    return (
      <BaseModalContainer>
        <ErrorState error={error} onButtonClick={reset} />
      </BaseModalContainer>
    );
  }

  const { userId } = data;
  const handleDeleteAccount = async () => {
    await mutateAsync(userId, {
      onSuccess: () => {
        const queryParam = new URLSearchParams();
        queryParam.set(
          QUERY_PARAM_KEYS.MODAL,
          QUERY_PARAM_MODAL_VALUE.PERSONALIZATION_REMOVED_DISCLAIMER,
        );
        onClose();
        StandaloneAuth.client.logout({
          federated: true,
          returnTo: `${window.location.origin}?${queryParam.toString()}`,
        });
      },
    });
  };

  return (
    <BaseModalContainer
      footer={
        <StackLayout
          horizontalAlignment={{ '@initial': 'end', '@mobile': 'center' }}
          orientation={{
            '@initial': 'horizontal',
            '@mobile': 'vertical',
          }}
          spacing="$one"
          css={{
            flexGrow: 0,
            '@mobile': {
              flexFlow: 'column-reverse',
            },
          }}
        >
          <ModalActionButton
            actionType="cancel"
            buttonText={formatMessage({ id: 'STR_NEVER_MIND' })}
            isMobile={isMobile}
            css={{ color: '$secondaryTextDefault' }}
            trackAnalytics={{
              product_area: PRODUCT_AREA.SETTINGS,
              screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
              detail: DETAIL.CANCEL,
            }}
          />
          <ModalActionButton
            actionType="submit"
            onClick={handleDeleteAccount}
            buttonText={formatMessage({ id: 'STR_YES_DELETE_ACCOUNT' })}
            isMobile={isMobile}
            destructive
            loading={isAccountDeleteLoading}
            disabled={isAccountDeleteLoading}
            trackAnalytics={{
              product_area: PRODUCT_AREA.SETTINGS,
              screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
              detail: DETAIL.CONFIRM_DELETION,
            }}
          />
        </StackLayout>
      }
    >
      <ImageMessage
        image={exitWarning}
        css={{
          flexGrow: 1,
          padding: '$two $half $two $half',
          textAlign: 'left',
          '@mobile': { padding: '$half' },
        }}
      >
        <HeadingText
          level="2"
          size={{
            '@initial': 'lg',
            '@mobile': 'xl',
          }}
          css={{
            paddingBlock: '$one',
          }}
        >
          {formatMessage({ id: 'STR_ARE_YOU_SURE' })}
        </HeadingText>
        <ParagraphText>
          {formatMessage({ id: 'STR_DELETE_ACCOUNT_BODY_1' })}
        </ParagraphText>
        <ParagraphText
          css={{
            marginBlock: '$one',
            '@mobile': {
              marginBlock: '$none',
            },
          }}
        >
          {formatMessage({ id: 'STR_DELETE_ACCOUNT_BODY_2' })}
        </ParagraphText>
      </ImageMessage>
    </BaseModalContainer>
  );
};
